<template>
    <div class="entryDetail">
        <div class="details">
            <Heading :class="[{ extraMargin: !entry.content.subtitle.en }]"
                :h="2"
                size="xl">
                <template v-if="title">
                    {{ title }}
                </template>
                <template v-else>
                    {{ siteContent.titleOfYourEntry }}
                </template>
            </Heading>
            <Heading
                v-if="subtitle"
                :h="3"
                font="secondary"
                size="s">
                {{ subtitle }}
            </Heading>

            <Contributor
                :slug="entry.creator.artist.slug"
                :avatar="entry.creator.artist.avatar"
                :name="entry.creator.artist.name">
            </Contributor>

            <p class="prologue">
                <span class="translationDisclaimer"
                    v-if="!entry.content.intro.hu && preferences.language === 'hu'">
                    {{ siteContent.notTranslatedToHungarian }}
                </span>
                <template v-if="intro">
                    {{ intro }}
                </template>
                <template v-else-if="introPlaceholder">
                    {{ introPlaceholder }}
                </template>
            </p>
            <p class="comments"
                v-if="activity.commentCount + activity.replyCount > 0">
                <template v-if="preferences.language === 'hu'">
                    {{ siteContent.allThe }} {{ activity.commentCount + activity.replyCount }} {{ siteContent.toComment }}
                </template>
                <template v-else>
                    {{ siteContent.read }} {{ activity.commentCount + activity.replyCount }} {{ commentLabel }}
                </template>
            </p>
        </div>
        <LikeButton class="likes"
            v-if="entry.id && entry.flag.visibility"
            :entry="entry"
            :likedBy="social.likedBy.users"/>
    </div>
</template>

<script>
    import { db } from '@/firebase'

    import Contributor from '@/components/entry/Contributor'
    import LikeButton  from '@/components/templates/LikeButton'

    export default {
        name: 'EntryDetail',
        components: {
            Contributor,
            LikeButton
        },
        props: {
            entry: {
                type: Object,
                required: true
            },
            introPlaceholder: {
                type: String,
            }
        },
        data() {
            return {
                activity: {
                    commentCount: null,
                    replyCount:   null
                },
                social: {
                    likedBy: {
                        users: []
                    }
                }
            }
        },
        computed: {
            title() {
                if(this.preferences.language === 'hu' && this.entry.content.title.hu) {
                    return this.entry.content.title.hu
                } else {
                    return this.entry.content.title.en
                }
            },
            subtitle() {
                if(this.preferences.language === 'hu' && this.entry.content.subtitle.hu) {
                    return this.entry.content.subtitle.hu
                } else {
                    return this.entry.content.subtitle.en
                }
            },
            intro() {
                if(this.preferences.language === 'hu' && this.entry.content.intro.hu) {
                    return this.entry.content.intro.hu
                } else {
                    return this.entry.content.intro.en
                }
            },
            avatar() {
                if(this.entry.artist.avatar) {
                    return this.entry.artist.avatar
                } else {
                    return this.constants.defaults.avatar
                }
            },
            commentLabel() {
                if(this.activity.commentCount + this.activity.replyCount === 1) {
                    return this.siteContent.comment
                } else {
                    return this.siteContent.comments
                }
            }
        },
        async mounted() {
            // Entry doesn't have id in creation modal
            if(this.entry.id) {
                // Calculate comment count
                try {
                    const commentsRef = await db
                        .collection('entryComments')
                        .where('doc', '==', this.entry.id)
                        .get()

                    this.activity.commentCount = commentsRef.size

                    const commentsData = commentsRef.docs.map(comment => ({
                        id: comment.id,
                        ...comment.data()
                    }))
                    
                    for(let i = 0; i < this.activity.commentCount; i++) {
                        const repliesRef = await db
                            .collection('entryComments')
                            .doc(commentsData[i].id)
                            .collection('replies')
                            .get()

                        this.activity.replyCount = repliesRef.size
                    }
                } catch(error) {
                    throw error
                }

                // Fetch likedBy array
                try {
                    await db
                        .collection('entries')
                        .doc(this.entry.id)
                        .collection('social')
                        .doc('likedBy')
                        .onSnapshot((doc) => {
                            const users = doc.data()
                            this.social.likedBy = users

                            return () => cleanUp()
                        })
                } catch(error) {
                    throw error
                }
            }
        }
    }
</script>

<style scoped lang="scss">
    @import '../../../scss/variables';

    .entryDetail {
        display: flex;
        flex-direction: column;
        position: relative;
        justify-content: space-between;
        height: 100%;
    }

    .details {
        position: relative;
        display: flex;
        flex-direction: column;
    }

    .prologue {
        position: relative;
        font-size: 1.075rem;
        margin: -0.5rem 0 0 0;
        white-space: pre-line;
    }

    .actions {
        display: flex;
        flex-wrap: wrap;
        margin-top: 2.5rem;
    }

    .h2 {
        margin: 0;
    }

    .extraMargin {
        margin-bottom: 1.5rem;
    }

    .h3 {
        margin: 0 0 1rem 0;
        color: var(--color-grey-500);
    }

    .translationDisclaimer {
        font-size: 0.875rem;
        margin-top: 2rem;
        color: var(--color-grey-600);
    }

    .social {
        display: flex;
        flex-direction: column;
    }

    .comments {
        font-size: 1rem;
        color: var(--color-grey-600);
        margin: 1rem 0 0 0;
    }

    .likes {
        margin-top: 0.5rem;
    }
</style>